import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { Package } from '../../services/projectService';
import contractorService, { Contractor } from '../../services/contractorService';
import { searchGoogleContacts, GoogleContactSuggestion } from '../../services/contactsService';

// Interfejs dla lokalnego formatu zapytania (kompatybilny z UI)
interface InquiryUI {
  id: string;
  packageId: string;
  packageName: string;
  contractorEmail: string;
  subject: string;
  status: string; // Używamy string zamiast union type, aby obsłużyć wszystkie możliwe statusy
  createdAt: string;
  inviteToCollectifileBoard?: boolean;
}

interface AddInquiryFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (inquiryData: InquiryUI) => void;
  onEdit?: (inquiryData: InquiryUI) => void;
  editMode?: boolean;
  inquiryData?: InquiryUI | null;
  projectName: string;
  packages: Package[];
  initialPackage?: Package | null;
  hasCollectifileBoard?: boolean;
}

const AddInquiryForm: React.FC<AddInquiryFormProps> = ({
  open,
  onClose,
  onAdd,
  onEdit,
  editMode = false,
  inquiryData = null,
  projectName,
  packages,
  initialPackage = null,
  hasCollectifileBoard = false
}) => {
  const [formData, setFormData] = useState({
    packageId: '',
    contractorEmail: '',
    subject: '',
    inviteToCollectifileBoard: false
  });
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Nowe stany dla wyszukiwania kontrahentów
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Sugestie e‑maili z Google Contacts
  const [emailSuggestions, setEmailSuggestions] = useState<GoogleContactSuggestion[]>([]);
  const [loadingEmailSuggestions, setLoadingEmailSuggestions] = useState(false);

  // Wypełnij formularz danymi zapytania, jeśli jest w trybie edycji lub jeśli podano initialPackage
  useEffect(() => {
    if (editMode && inquiryData) {
      const foundPackage = packages.find(pkg => pkg.package_id === inquiryData.packageId);
      setSelectedPackage(foundPackage || null);
      
      setFormData({
        packageId: inquiryData.packageId || '',
        contractorEmail: inquiryData.contractorEmail || '',
        subject: inquiryData.subject || '',
        inviteToCollectifileBoard: !!inquiryData.inviteToCollectifileBoard
      });
    } else if (initialPackage) {
      // Jeśli podano initialPackage, ustaw go jako wybrany pakiet
      setSelectedPackage(initialPackage);
      
      // Usunięcie pierwszych 8 znaków z nazwy projektu, jeśli zaczyna się od numeru projektu
      const shortProjectName = projectName.match(/^\d{3}-\d{3}\s(.+)$/) 
        ? projectName.match(/^\d{3}-\d{3}\s(.+)$/)?.[1] || projectName 
        : projectName;
        
      const generatedSubject = `${shortProjectName}: Zapytanie ${initialPackage.name}`;
      
      setFormData({
        packageId: initialPackage.package_id,
        contractorEmail: '',
        subject: generatedSubject,
        inviteToCollectifileBoard: false
      });
    } else {
      // Reset formularza w trybie dodawania
      setSelectedPackage(null);
      setFormData({
        packageId: '',
        contractorEmail: '',
        subject: '',
        inviteToCollectifileBoard: false
      });
    }
  }, [editMode, inquiryData, packages, open, initialPackage, projectName]);

  // Generowanie tematu na podstawie wybranego pakietu
  useEffect(() => {
    if (selectedPackage) {
      // Usunięcie pierwszych 8 znaków z nazwy projektu, jeśli zaczyna się od numeru projektu
      const shortProjectName = projectName.match(/^\d{3}-\d{3}\s(.+)$/) 
        ? projectName.match(/^\d{3}-\d{3}\s(.+)$/)?.[1] || projectName 
        : projectName;
        
      const generatedSubject = `${shortProjectName}: Zapytanie ${selectedPackage.name}`;
      setFormData(prev => ({
        ...prev,
        packageId: selectedPackage.package_id,
        subject: generatedSubject
      }));
    }
  }, [selectedPackage, projectName]);

  // Funkcja do wyszukiwania kontrahentów
  const searchContractors = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      setLoadingContractors(true);
      const filters = { searchText: query };
      const response = await contractorService.filterContractors(filters, 1, 10);
      setSearchResults(response.contractors);
      setShowResults(true);
    } catch (error) {
      console.error('Błąd podczas wyszukiwania kontrahentów:', error);
      setSearchResults([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  // Obsługa wyboru kontrahenta z wyników wyszukiwania
  const handleSelectContractor = (contractor: Contractor) => {
    // Wybierz odpowiedni adres email
    let emailToUse = '';
    
    // Jeśli email jest "brak" lub "brak email" i istnieje email2, użyj email2
    if ((contractor.email === 'brak' || contractor.email === 'brak email' || 
         contractor.email === 'Brak email' || contractor.email === 'Brak') && 
        contractor.email2 && contractor.email2.trim() !== '') {
      emailToUse = contractor.email2;
    } else {
      // W przeciwnym razie użyj głównego adresu email
      emailToUse = contractor.email || '';
    }
    
    setFormData(prev => ({
      ...prev,
      contractorEmail: emailToUse
    }));
    
    setShowResults(false);
    setSearchQuery('');
  };

  const handlePackageChange = (event: React.SyntheticEvent, value: Package | null) => {
    setSelectedPackage(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Debounce wyszukiwania kontaktów Google po wpisanym adresie/nazwie
  useEffect(() => {
    const q = formData.contractorEmail?.trim() || '';
    if (q.length < 2) {
      setEmailSuggestions([]);
      return;
    }
    let active = true;
    setLoadingEmailSuggestions(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchGoogleContacts(q);
        if (active) setEmailSuggestions(data);
      } catch (err) {
        if (active) setEmailSuggestions([]);
      } finally {
        if (active) setLoadingEmailSuggestions(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [formData.contractorEmail]);

  const handleSubmit = () => {
    if (!selectedPackage) return;
    
    setLoading(true);

    // Przygotowanie danych zapytania
    const inquiryToSave: InquiryUI = {
      // W trybie edycji używamy istniejącego ID, w trybie dodawania używamy tymczasowego ID,
      // które zostanie zastąpione przez backend (format: Z.[sequence_number].[project_id].[package_id])
      id: editMode && inquiryData ? inquiryData.id : `TEMP-${Date.now().toString().slice(-6)}`,
      packageId: formData.packageId,
      packageName: selectedPackage.name,
      contractorEmail: formData.contractorEmail,
      subject: formData.subject,
      status: 'przygotowane',
      createdAt: new Date().toISOString(),
      inviteToCollectifileBoard: formData.inviteToCollectifileBoard
    };

    console.log('Dane zapytania do zapisania:', inquiryToSave);

    try {
      if (editMode && onEdit) {
        onEdit(inquiryToSave);
      } else {
        onAdd(inquiryToSave);
      }
    } catch (error) {
      console.error('Błąd podczas zapisywania zapytania:', error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#004273', color: 'white' }}>
        {editMode ? 'Edytuj zapytanie' : 'Dodaj nowe zapytanie'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Dane zapytania
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Autocomplete
              options={packages}
              getOptionLabel={(option) => `${option.package_symbol} - ${option.name}`}
              value={selectedPackage}
              onChange={handlePackageChange}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Wybierz pakiet" 
                  size="small"
                  required
                  placeholder="Wybierz pakiet z listy"
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            {/* Pole wyszukiwania kontrahenta */}
            <Box sx={{ mb: 2, position: 'relative' }}>
              <TextField
                fullWidth
                label="Wyszukaj kontrahenta"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchContractors(e.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {loadingContractors ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Search />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Wyniki wyszukiwania */}
              {showResults && searchResults.length > 0 && (
                <Paper 
                  elevation={3} 
                  sx={{ 
                    position: 'absolute', 
                    width: '100%', 
                    maxHeight: '200px', 
                    overflow: 'auto',
                    zIndex: 1000,
                    mt: 0.5
                  }}
                >
                  <List dense>
                    {searchResults.map((contractor) => (
                      <React.Fragment key={contractor.id}>
                        <ListItem 
                          button 
                          onClick={() => handleSelectContractor(contractor)}
                        >
                          <ListItemText 
                            primary={contractor.name} 
                            secondary={
                              <>
                                <Typography variant="body2" component="span">
                                  {contractor.email === 'brak' || contractor.email === 'brak email' || 
                                   contractor.email === 'Brak email' || contractor.email === 'Brak' ? (
                                    contractor.email2 && contractor.email2.trim() !== '' ? (
                                      <>{contractor.email2} <span style={{ color: '#999' }}>(dodatkowy)</span></>
                                    ) : (
                                      <>brak email</>
                                    )
                                  ) : (
                                    <>
                                      {contractor.email}
                                      {contractor.email2 && contractor.email2.trim() !== '' && (
                                        <>, {contractor.email2} <span style={{ color: '#999' }}>(dodatkowy)</span></>
                                      )}
                                    </>
                                  )}
                                </Typography>
                                <br />
                                <Typography variant="body2" component="span" color="text.secondary">
                                  {contractor.location}, NIP: {contractor.nip}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>

            <Autocomplete
              freeSolo
              options={emailSuggestions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.email}
              filterOptions={(x) => x}
              loading={loadingEmailSuggestions}
              inputValue={formData.contractorEmail}
              onChange={(event, value) => {
                if (typeof value === 'string') {
                  setFormData((prev) => ({ ...prev, contractorEmail: value }));
                } else if (value) {
                  setFormData((prev) => ({ ...prev, contractorEmail: value.email }));
                }
              }}
              onInputChange={(event, value) => {
                setFormData((prev) => ({ ...prev, contractorEmail: value }));
              }}
              isOptionEqualToValue={(opt, val) => {
                const o = typeof opt === 'string' ? opt : opt.email;
                const v = typeof val === 'string' ? val : val.email;
                return o === v;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Adres e-mail kontrahenta"
                  name="contractorEmail"
                  type="email"
                  required
                  variant="outlined"
                  size="small"
                  placeholder="kontrahent@example.com"
                  autoComplete="off"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                    spellCheck: false,
                    autoCorrect: 'off',
                    autoCapitalize: 'off'
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {loadingEmailSuggestions ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </InputAdornment>
                    )
                  }}
                />
              )}
              renderOption={(props, option) => {
                const opt = typeof option === 'string' ? { email: option, name: '' } : option;
                return (
                  <li {...props} key={opt.email}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{opt.name || opt.email}</Typography>
                      {opt.name && (
                        <Typography variant="caption" color="text.secondary">{opt.email}</Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Temat"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
            />
          </Grid>

          {hasCollectifileBoard && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.inviteToCollectifileBoard}
                    onChange={(e) => setFormData(prev => ({ ...prev, inviteToCollectifileBoard: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Zaproś do boardu Collectifile"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ mr: 1 }}
        >
          Anuluj
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!selectedPackage || !formData.contractorEmail}
          sx={{ 
            backgroundColor: '#004273',
            '&:hover': {
              backgroundColor: '#003660'
            }
          }}
        >
          {editMode ? 'Zapisz zmiany' : 'Dodaj zapytanie'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInquiryForm;
